import React, { useCallback, useState } from 'react';
import { Mail, MailPlus, Save, RefreshCw, Link, AlertCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicEmailSection from './BasicEmailSection';
import BodyContentSection from './BodyContentSection';
import AdvancedOptionsSection from './AdvancedOptionsSection';
import SignatureSection from './SignatureSection';
import TrackingSection from './TrackingSection';

/**
 * Компонент основной формы генератора mailto-ссылок
 */
const GeneratorForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isGenerating, 
  setIsGenerating,
  setMailtoLink,
  generateCodes,
  emailTemplates,
  saveTemplate 
}) => {
  
  // Состояние для имени сохраняемого шаблона
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeGoogleAnalytics' && !checked) {
        newState.gaTrackingId = '';
        newState.trackOpens = false;
      }
      
      if (name === 'useUtmParameters' && !checked) {
        newState.utmSource = 'email';
        newState.utmMedium = 'mailto';
        newState.utmCampaign = '';
      }
      
      if (name === 'addSignature' && !checked) {
        newState.signature = '';
        newState.companyName = '';
        newState.companyWebsite = '';
        newState.companyPhone = '';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const { to, subject, body, includeGoogleAnalytics, gaTrackingId, useUtmParameters, utmCampaign, companyWebsite, companyPhone } = formData;
    
    // Валидация базовых полей
    if (!to) {
      newErrors.to = 'Укажите email-адрес получателя.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to) && !to.includes(',')) {
      newErrors.to = 'Введите корректный email-адрес.';
    } else if (to.includes(',')) {
      // Проверка каждого адреса в списке с запятыми
      const addresses = to.split(',').map(a => a.trim());
      for (const addr of addresses) {
        if (addr && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) {
          newErrors.to = 'Один или несколько email-адресов некорректны.';
          break;
        }
      }
    }
    
    // Валидация Google Analytics
    if (includeGoogleAnalytics && !gaTrackingId) {
      newErrors.gaTrackingId = 'Введите ID Google Analytics.';
    } else if (includeGoogleAnalytics && !/^UA-\d{4,10}-\d{1,4}$|^G-[A-Z0-9]{10}$/.test(gaTrackingId)) {
      newErrors.gaTrackingId = 'Формат ID должен быть UA-XXXXXXXX-X или G-XXXXXXXXXX.';
    }
    
    // Валидация UTM-меток
    if (useUtmParameters && !utmCampaign) {
      newErrors.utmCampaign = 'Укажите название кампании.';
    }
    
    // Валидация URL сайта компании
    if (companyWebsite && !/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-.\/?%&=]*)?$/.test(companyWebsite)) {
      newErrors.companyWebsite = 'Введите корректный URL сайта.';
    }
    
    // Валидация телефона компании
    if (companyPhone && !/^(\+7|8)[\s\-]?\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{2}[\s\-]?\d{2}$/.test(companyPhone.replace(/\s/g, ''))) {
      newErrors.companyPhone = 'Введите корректный номер телефона в формате +7 (XXX) XXX-XX-XX.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция энкодинга текста для mailto-ссылки
  const encodeMailtoText = (text, useCyrillic = true) => {
    if (!text) return '';
    
    if (useCyrillic) {
      // Для кириллицы используем encodeURI
      return encodeURI(text);
    } else {
      // Для полного преобразования в ASCII используем encodeURIComponent
      return encodeURIComponent(text);
    }
  };

  // Генерация mailto-ссылки
  const generateMailtoLink = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsGenerating(true);
    setMailtoLink('');

    // Используем setTimeout для обновления UI перед генерацией
    setTimeout(() => {
      try {
        const {
          to, cc, bcc, subject, body,
          includeGoogleAnalytics, gaTrackingId, trackOpens,
          useUtmParameters, utmSource, utmMedium, utmCampaign,
          useCyrillicEncoding, addLineBreaks, addSignature,
          signature, companyName, companyWebsite, companyPhone
        } = formData;

        // Формируем базовую часть ссылки с адресом получателя
        let mailtoUrl = `mailto:${to}`;
        
        // Готовим параметры
        const params = [];
        
        // Добавляем копии (cc)
        if (cc) {
          params.push(`cc=${encodeMailtoText(cc, useCyrillicEncoding)}`);
        }
        
        // Добавляем скрытые копии (bcc)
        if (bcc) {
          params.push(`bcc=${encodeMailtoText(bcc, useCyrillicEncoding)}`);
        }
        
        // Добавляем тему письма
        if (subject) {
          params.push(`subject=${encodeMailtoText(subject, useCyrillicEncoding)}`);
        }
        
        // Формируем содержимое письма
        let fullBody = body || '';
        
        // Добавляем переносы строк, если выбрано
        if (addLineBreaks && fullBody) {
          fullBody = fullBody.replace(/\\n/g, '\n').replace(/\n/g, '%0D%0A');
        }
        
        // Добавляем подпись, если выбрано
        if (addSignature && signature) {
          let signatureBlock = `\n\n-- \n${signature}`;
          
          if (companyName) {
            signatureBlock += `\n${companyName}`;
          }
          
          if (companyPhone) {
            signatureBlock += `\n${companyPhone}`;
          }
          
          if (companyWebsite) {
            signatureBlock += `\n${companyWebsite}`;
          }
          
          // Заменяем переносы строк для URL
          signatureBlock = signatureBlock.replace(/\n/g, '%0D%0A');
          fullBody += signatureBlock;
        }
        
        // Добавляем UTM-метки, если выбрано
        if (useUtmParameters && utmCampaign) {
          let utmParams = `\n\n--\nОтслеживание: utm_source=${utmSource}&utm_medium=${utmMedium}&utm_campaign=${utmCampaign}`;
          utmParams = utmParams.replace(/\n/g, '%0D%0A');
          fullBody += utmParams;
        }
        
        // Добавляем пиксель отслеживания открытий, если выбрано
        if (includeGoogleAnalytics && trackOpens) {
          fullBody += '%0D%0A%0D%0A<img src="https://www.google-analytics.com/collect?v=1&tid=' + 
                      gaTrackingId + '&cid=555&t=event&ec=email&ea=open&el=mailto" />';
        }
        
        // Добавляем тело письма к параметрам
        if (fullBody) {
          params.push(`body=${encodeMailtoText(fullBody, useCyrillicEncoding)}`);
        }
        
        // Собираем финальную ссылку
        if (params.length > 0) {
          mailtoUrl += '?' + params.join('&');
        }
        
        // Устанавливаем результат
        setMailtoLink(mailtoUrl);
        
        // Генерируем HTML и Markdown коды
        generateCodes(mailtoUrl);
      } catch (error) {
        console.error("Ошибка генерации:", error);
        setErrors({ general: `Произошла ошибка при генерации ссылки: ${error.message}. Пожалуйста, проверьте введенные данные.` });
    } finally {
        setIsGenerating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setMailtoLink, generateCodes, setIsGenerating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
      includeGoogleAnalytics: false,
      gaTrackingId: '',
      trackOpens: false,
      useUtmParameters: false,
      utmSource: 'email',
      utmMedium: 'mailto',
      utmCampaign: '',
      useCyrillicEncoding: true,
      addLineBreaks: true,
      addSignature: false,
      signature: '',
      companyName: '',
      companyWebsite: '',
      companyPhone: '',
    });
    setMailtoLink('');
    setErrors({});
    setSaveTemplateName('');
    setShowSaveDialog(false);
  };

  // Загрузка шаблона
  const loadTemplate = (templateId) => {
    const template = emailTemplates.find(template => template.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        subject: template.subject,
        body: template.body
      }));
    }
  };

  // Обработчик сохранения шаблона
  const handleSaveTemplate = () => {
    if (showSaveDialog) {
      const savedResult = saveTemplate(saveTemplateName || null);
      if (savedResult) {
        alert(`Шаблон "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveTemplateName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    generateMailtoLink();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Mail className="w-6 h-6 text-indigo-500" />
          Параметры email-ссылки
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Информационное сообщение */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-md text-blue-700 dark:text-blue-300 text-sm flex items-start">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">О генераторе Mailto-ссылок</p>
            <p>Mailto-ссылки используются для создания ссылок, открывающих почтовый клиент пользователя с заранее заполненными полями (адресат, тема, текст письма и т.д.). Это полезно для форм обратной связи, контактных страниц и email-маркетинга.</p>
          </div>
        </div>

        {/* Основные параметры email */}
        <BasicEmailSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Содержимое письма */}
        <BodyContentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          loadTemplate={loadTemplate}
          emailTemplates={emailTemplates}
        />

        {/* Дополнительные параметры */}
        <AdvancedOptionsSection 
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Отслеживание */}
        <TrackingSection 
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Подпись */}
        <SignatureSection 
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Диалог сохранения шаблона */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveTemplateName}
                onChange={(e) => setSaveTemplateName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveTemplate}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Link className="w-5 h-5" />}
            disabled={isGenerating || Object.keys(errors).length > 0}
            isLoading={isGenerating}
            size="lg"
          >
            {isGenerating ? 'Генерируем...' : 'Создать mailto-ссылку'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isGenerating}
            size="lg"
          >
            Сбросить
          </Button>
          {!showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveTemplate}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isGenerating || !formData.to}
              size="lg"
            >
              Сохранить шаблон
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default GeneratorForm;